import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CampaignsCarouselWidgetProps,
  type CampaignsCarouselThemingProps,
  CAROUSEL_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCampaignsCarousel for imperative control.
 */
export interface LegionCampaignsCarouselHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildCampaignsCarouselWidgetUrl(
  props: CampaignsCarouselWidgetProps
): string {
  const {
    baseUrl,
    apiKey,
    maxCampaigns = 5,
    showExpiration = true,
    theme = "light",
    height = 280,
  } = props;

  const url = new URL("/widget/campaigns-carousel", baseUrl);
  url.searchParams.set("maxCampaigns", String(maxCampaigns));
  url.searchParams.set("showExpiration", String(showExpiration));
  url.searchParams.set("theme", theme);
  url.searchParams.set("height", String(height));

  if (apiKey) {
    url.searchParams.set("apiKey", apiKey);
  }

  // Append theming params (camelCase, not kebab-case)
  const themingKeys = Object.keys(CAROUSEL_THEMING_PROP_TO_PARAM) as Array<
    keyof CampaignsCarouselThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(CAROUSEL_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionCampaignsCarousel – a React component that embeds the Legion
 * Campaigns Carousel widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionCampaignsCarousel } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionCampaignsCarousel
 *       baseUrl="https://your-legion-domain.com"
 *       apiKey="your-api-key"
 *       maxCampaigns={5}
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionCampaignsCarousel = forwardRef<
  LegionCampaignsCarouselHandle,
  CampaignsCarouselWidgetProps
>(function LegionCampaignsCarousel(props, ref) {
  const {
    maxCampaigns = 5,
    showExpiration = true,
    theme = "light",
    height = 280,
    apiKey,
    width = "100%",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildCampaignsCarouselWidgetUrl(props),
    [
      baseUrl,
      apiKey,
      maxCampaigns,
      showExpiration,
      theme,
      height,
      ...Object.keys(CAROUSEL_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    onLoad?.({ widget: "campaigns-carousel", maxCampaigns, theme });
  }, [maxCampaigns, theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load campaigns carousel widget");
  }, [onError]);

  const resolvedHeight = `${height}px`;

  return (
    <div
      className={className}
      style={{ width, height: resolvedHeight, ...style }}
      data-testid="legion-campaigns-carousel"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Campaigns Carousel Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});

